import { useState, useEffect, useMemo, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  ChevronLeft,
  ChevronDown,
  ShoppingCart,
  Plus,
  Minus,
  X,
  Tag,
  Copy,
  Check,
  CreditCard,
  Loader2,
  ArrowRight,
  Coins,
  Info,
  Package,
  Search,
  Gift,
  Users,
  Building2,
  Sparkles,
  Globe,
  MapPin,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { bonndleAuthApi } from '../services/bonndleAuthApi';
import { defaultsApi, paymentApi } from '../services/api';
import { getStoredUser, getAuthToken, setAuthToken } from '../services/bonndleApi';

const API_BASE = import.meta.env.VITE_API_URL || 'https://boldscaleservices.com';
const API_ENV = API_BASE.includes('staging') ? 'staging' : 'production';
const PROXY_BASE = '/api';

// Gifftins color theme - cream, purple, gold
const THEME = {
  primary: '#7C3AED',
  primaryDark: '#6D28D9',
  primaryLight: '#A78BFA',
  gold: '#F59E0B',
  goldLight: '#FCD34D',
  cream: '#F3F4F6',
  creamDark: '#FAFAFA',
  // For gradient compatibility
  gradientStart: '#7C3AED',
  gradientEnd: '#6D28D9',
  lilac: '#E9D5FF',
  ink: '#1F2937',
  canvas: '#F3F4F6',
  accent: '#F59E0B',
};

// Gifftins logo
const GIFFTINS_LOGO = '/icons/Gifftins_Logo.PNG';

// Country code mapping for flags
const COUNTRY_CODES = {
  'Nigeria': 'NG', 'Ghana': 'GH', 'Kenya': 'KE', 'South Africa': 'ZA', 'Egypt': 'EG',
  'United States': 'US', 'USA': 'US', 'United States of America': 'US',
  'Canada': 'CA', 'Mexico': 'MX', 'Brazil': 'BR',
  'United Kingdom': 'GB', 'UK': 'GB', 'Germany': 'DE', 'France': 'FR',
  'Italy': 'IT', 'Spain': 'ES', 'Netherlands': 'NL', 'Belgium': 'BE',
  'Australia': 'AU', 'New Zealand': 'NZ', 'Japan': 'JP', 'South Korea': 'KR',
  'China': 'CN', 'India': 'IN', 'Singapore': 'SG', 'Malaysia': 'MY',
  'Indonesia': 'ID', 'Philippines': 'PH', 'Thailand': 'TH', 'Vietnam': 'VN',
  'Saudi Arabia': 'SA', 'United Arab Emirates': 'AE', 'UAE': 'AE',
  'Turkey': 'TR', 'Poland': 'PL', 'Sweden': 'SE', 'Norway': 'NO',
  'Denmark': 'DK', 'Finland': 'FI', 'Switzerland': 'CH', 'Austria': 'AT',
  'Portugal': 'PT', 'Ireland': 'IE', 'Greece': 'GR', 'Czech Republic': 'CZ',
  'Romania': 'RO', 'Hungary': 'HU', 'Argentina': 'AR', 'Colombia': 'CO',
  'Chile': 'CL', 'Peru': 'PE', 'Algeria': 'DZ', 'Morocco': 'MA',
  'Tunisia': 'TN', 'Cameroon': 'CM', 'Senegal': 'SN', 'Ivory Coast': 'CI',
};

// Country to currency mapping
const COUNTRY_CURRENCY = {
  'Nigeria': 'NGN',
  'United States': 'USD',
  'USA': 'USD',
  'United States of America': 'USD',
  'United Kingdom': 'GBP',
  'UK': 'GBP',
  'Germany': 'EUR',
  'France': 'EUR',
  'Italy': 'EUR',
  'Spain': 'EUR',
  'Netherlands': 'EUR',
  'Canada': 'CAD',
  'Australia': 'AUD',
  'Japan': 'JPY',
  'India': 'INR',
  'South Africa': 'ZAR',
  'Ghana': 'GHS',
  'Kenya': 'KES',
};

// Exchange rates to USD (approximate - in production, use a live API)
const EXCHANGE_RATES_TO_USD = {
  'USD': 1,
  'NGN': 1550,    // 1 USD = ~1550 NGN
  'GBP': 0.79,    // 1 USD = ~0.79 GBP
  'EUR': 0.92,    // 1 USD = ~0.92 EUR
  'CAD': 1.36,    // 1 USD = ~1.36 CAD
  'AUD': 1.53,    // 1 USD = ~1.53 AUD
  'JPY': 154,     // 1 USD = ~154 JPY
  'INR': 83.5,    // 1 USD = ~83.5 INR
  'ZAR': 18.5,    // 1 USD = ~18.5 ZAR
  'GHS': 15.5,    // 1 USD = ~15.5 GHS
  'KES': 153,     // 1 USD = ~153 KES
};

// Currency symbols
const CURRENCY_SYMBOLS = {
  'USD': '$',
  'NGN': '₦',
  'GBP': '£',
  'EUR': '€',
  'CAD': 'C$',
  'AUD': 'A$',
  'JPY': '¥',
  'INR': '₹',
  'ZAR': 'R',
  'GHS': 'GH₵',
  'KES': 'KSh',
};

// Timezone to country mapping for fallback detection
const TIMEZONE_TO_COUNTRY = {
  'Africa/Lagos': 'Nigeria',
  'Africa/Abuja': 'Nigeria',
  'Africa/Accra': 'Ghana',
  'Africa/Nairobi': 'Kenya',
  'Africa/Johannesburg': 'South Africa',
  'Africa/Cairo': 'Egypt',
  'Africa/Casablanca': 'Morocco',
  'Africa/Douala': 'Cameroon',
  'America/New_York': 'United States',
  'America/Los_Angeles': 'United States',
  'America/Chicago': 'United States',
  'America/Denver': 'United States',
  'America/Phoenix': 'United States',
  'America/Toronto': 'Canada',
  'America/Vancouver': 'Canada',
  'America/Mexico_City': 'Mexico',
  'America/Sao_Paulo': 'Brazil',
  'Europe/London': 'United Kingdom',
  'Europe/Paris': 'France',
  'Europe/Berlin': 'Germany',
  'Europe/Rome': 'Italy',
  'Europe/Madrid': 'Spain',
  'Europe/Amsterdam': 'Netherlands',
  'Europe/Brussels': 'Belgium',
  'Europe/Vienna': 'Austria',
  'Europe/Lisbon': 'Portugal',
  'Europe/Dublin': 'Ireland',
  'Australia/Sydney': 'Australia',
  'Australia/Melbourne': 'Australia',
  'Pacific/Auckland': 'New Zealand',
  'Asia/Tokyo': 'Japan',
  'Asia/Seoul': 'South Korea',
  'Asia/Shanghai': 'China',
  'Asia/Kolkata': 'India',
  'Asia/Singapore': 'Singapore',
  'Asia/Kuala_Lumpur': 'Malaysia',
  'Asia/Jakarta': 'Indonesia',
  'Asia/Manila': 'Philippines',
  'Asia/Bangkok': 'Thailand',
  'Asia/Ho_Chi_Minh': 'Vietnam',
  'Asia/Dubai': 'United Arab Emirates',
  'Asia/Riyadh': 'Saudi Arabia',
};

// Get country from browser timezone (fallback)
const getCountryFromTimezone = () => {
  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    console.log('[Gifftins] Browser timezone:', timezone);
    return TIMEZONE_TO_COUNTRY[timezone] || null;
  } catch (error) {
    console.error('[Gifftins] Timezone detection failed:', error);
    return null;
  }
};

// IP Geolocation API helper with timezone fallback
const detectUserCountry = async () => {
  // Try IP-based geolocation first
  try {
    const response = await fetch('https://ipapi.co/json/');
    if (response.ok) {
      const data = await response.json();
      if (data.country_name) {
        console.log('[Gifftins] IP geolocation detected:', data.country_name);
        return data.country_name;
      }
    }
  } catch (error) {
    console.log('[Gifftins] ipapi.co failed, trying fallback...');
  }

  try {
    const response = await fetch('http://ip-api.com/json/');
    if (response.ok) {
      const data = await response.json();
      if (data.country) {
        console.log('[Gifftins] IP fallback detected:', data.country);
        return data.country;
      }
    }
  } catch (error) {
    console.log('[Gifftins] ip-api.com failed, trying timezone...');
  }

  // Fallback to timezone-based detection
  const timezoneCountry = getCountryFromTimezone();
  if (timezoneCountry) {
    console.log('[Gifftins] Timezone fallback detected:', timezoneCountry);
    return timezoneCountry;
  }

  console.log('[Gifftins] All geolocation methods failed, defaulting to United States');
  return 'United States';
};

const getCountryCurrency = (countryName) => {
  if (!countryName) return 'USD';
  return COUNTRY_CURRENCY[countryName] || 'USD';
};

const convertCurrency = (amount, fromCurrency, toCurrency) => {
  if (fromCurrency === toCurrency) return amount;
  const fromRate = EXCHANGE_RATES_TO_USD[fromCurrency] || 1;
  const toRate = EXCHANGE_RATES_TO_USD[toCurrency] || 1;
  // Convert to USD first, then to target currency
  const usdAmount = amount / fromRate;
  return usdAmount * toRate;
};

const formatLocalCurrency = (amount, currency) => {
  const symbol = CURRENCY_SYMBOLS[currency] || currency;
  const formatted = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);
  return `${symbol}${formatted}`;
};

// List of countries for recipient selection
const RECIPIENT_COUNTRIES = [
  'Nigeria', 'United States', 'United Kingdom', 'Canada', 'Ghana', 'Kenya',
  'South Africa', 'Germany', 'France', 'Australia', 'India', 'Japan',
];

const getCountryCode = (countryName) => {
  if (!countryName) return null;
  if (COUNTRY_CODES[countryName]) return COUNTRY_CODES[countryName];
  const lowerName = countryName.toLowerCase();
  for (const [country, code] of Object.entries(COUNTRY_CODES)) {
    if (country.toLowerCase() === lowerName) return code;
  }
  return null;
};

const getCountryFlag = (countryCode) => {
  if (!countryCode || countryCode.length !== 2) return '🌍';
  const codePoints = countryCode.toUpperCase().split('').map(char => 127397 + char.charCodeAt(0));
  return String.fromCodePoint(...codePoints);
};

function getUserToken(urlToken = null) {
  if (urlToken) return urlToken;
  return getAuthToken() || localStorage.getItem('bonndle_access_token') || localStorage.getItem('bonndle_token');
}

function formatCurrency(amount, currency = 'NGN') {
  if (currency === 'USD') {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(amount);
  }
  return new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN', minimumFractionDigits: 0 }).format(amount);
}

export default function GifftinsStorePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const cashbackCodeFromUrl = searchParams.get('code');
  const authTokenFromUrl = searchParams.get('authToken');
  const isEmbeddedFlutter = searchParams.get('embedded') === 'flutter';

  useEffect(() => {
    if (authTokenFromUrl) setAuthToken(authTokenFromUrl);
  }, [authTokenFromUrl]);

  const FLUTTER_STORAGE_KEYS = {
    REWARDS_BALANCE: 'bonndle:rewards_balance',
    SAVED_CARDS: 'bonndle:saved_cards',
    FLUTTER_INJECTED: 'bonndle:flutter_injected',
  };

  const [flutterAuthReady, setFlutterAuthReady] = useState(false);

  // Provider states
  const [allProviders, setAllProviders] = useState([]);
  const [countries, setCountries] = useState([]);
  const [loadingProviders, setLoadingProviders] = useState(true);

  // Selection states
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);

  // Country detection states
  const [detectedCountry, setDetectedCountry] = useState(null);
  const [detectingLocation, setDetectingLocation] = useState(true);

  // Product states (for selected provider)
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);

  // Gift form states
  const [giftAmount, setGiftAmount] = useState('');
  const [recipientCountry, setRecipientCountry] = useState('United States');
  const [recipientName, setRecipientName] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [giftNote, setGiftNote] = useState('');
  const [showGiftConfirmation, setShowGiftConfirmation] = useState(false);

  // User's detected country for default selection
  const [userCountry, setUserCountry] = useState('United States');

  // Search states
  const [countrySearch, setCountrySearch] = useState('');
  const [providerSearch, setProviderSearch] = useState('');

  // Dropdown state
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const countryDropdownRef = useRef(null);

  // Cart states
  const [cart, setCart] = useState([]);
  const [showCart, setShowCart] = useState(false);
  const [cashbackCode, setCashbackCode] = useState(cashbackCodeFromUrl || '');
  const [appliedCashback, setAppliedCashback] = useState(null);
  const [copiedCode, setCopiedCode] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [transactionDetails, setTransactionDetails] = useState(null);

  // API states
  const [validatingCode, setValidatingCode] = useState(false);
  const [validationError, setValidationError] = useState(null);
  const [processingPayment, setProcessingPayment] = useState(false);

  // Payment states
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [savedCards, setSavedCards] = useState([]);
  const [cardsLoading, setCardsLoading] = useState(false);
  const [rewardsBalance, setRewardsBalance] = useState(0);
  const [addingCard, setAddingCard] = useState(false);

  const rewardsInUSD = useMemo(() => rewardsBalance / 1000, [rewardsBalance]);

  // Close country dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (countryDropdownRef.current && !countryDropdownRef.current.contains(event.target)) {
        setShowCountryDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Detect user's country on mount (IP-based with timezone fallback)
  useEffect(() => {
    const detectLocation = async () => {
      setDetectingLocation(true);
      try {
        const detectedCountryName = await detectUserCountry();
        if (detectedCountryName) {
          setDetectedCountry(detectedCountryName);
          setUserCountry(detectedCountryName);
          setRecipientCountry(detectedCountryName);
          console.log('[Gifftins] Auto-detected country:', detectedCountryName);
        }
      } catch (error) {
        console.error('[Gifftins] Location detection failed:', error);
      } finally {
        setDetectingLocation(false);
      }
    };
    detectLocation();
  }, []);

  // Auto-select country when detected and countries are loaded
  useEffect(() => {
    if (detectedCountry && countries.length > 0 && !selectedCountry) {
      // Find matching country in available countries that has USD gift cards
      const countriesWithUSD = countries.filter(c =>
        allProviders.some(p => p.country === c && p.currency === 'USD')
      );

      const matchedCountry = countriesWithUSD.find(c =>
        c.toLowerCase() === detectedCountry.toLowerCase() ||
        c.toLowerCase().includes(detectedCountry.toLowerCase()) ||
        detectedCountry.toLowerCase().includes(c.toLowerCase())
      );

      if (matchedCountry) {
        console.log('[Gifftins] Auto-selecting detected country:', matchedCountry);
        setSelectedCountry(matchedCountry);
      }
    }
  }, [detectedCountry, countries, selectedCountry, allProviders]);

  // Fetch gift card providers (category 13.x = eVouchers)
  useEffect(() => {
    const fetchProviders = async () => {
      setLoadingProviders(true);
      try {
        const response = await fetch(`${API_BASE}/providers?page=1&limit=2000`);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();
        const providers = Array.isArray(data) ? data : data.data || [];

        // Filter for gift card providers (category 13.x)
        const giftCardProviders = providers.filter(p =>
          p.product_categories?.some(cat => cat.startsWith('13'))
        );

        console.log('[GifftinsStore] Gift card providers:', giftCardProviders.length);
        setAllProviders(giftCardProviders);

        // Extract unique countries
        const uniqueCountries = [...new Set(giftCardProviders.map(p => p.country).filter(Boolean))].sort();
        setCountries(uniqueCountries);
      } catch (error) {
        console.error('[GifftinsStore] Error fetching providers:', error);
      } finally {
        setLoadingProviders(false);
      }
    };
    fetchProviders();
  }, []);

  // Fetch products when provider is selected
  useEffect(() => {
    if (!selectedProvider) {
      setProducts([]);
      return;
    }

    const fetchProducts = async () => {
      setProductsLoading(true);
      try {
        const response = await fetch(`${API_BASE}/products?providerId=${selectedProvider.provider_uuid}&page=1&limit=100`);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();

        const productList = data.all || data.data || (Array.isArray(data) ? data : []);
        console.log('[GifftinsStore] Products for provider:', productList.length);

        const transformedProducts = productList.map((item, index) => {
          // Extract actual USD amount from metadata.price.operator (e.g., "10.00" for $10)
          // The price field contains NGN equivalent, not USD
          const operatorPrice = item.metadata?.price?.operator;
          const usdAmount = operatorPrice ? parseFloat(operatorPrice) : (item.min_price || item.price || 0);

          // Clean title: strip "Gift Card" and just show amount (e.g., "10 USD" -> "10 USD")
          const rawName = item.name || 'Gift Card';
          const cleanTitle = rawName.replace(/\s*Gift Card\s*/gi, '').trim();

          return {
            id: item.product_uuid || item.uuid || `product_${index}`,
            title: cleanTitle || rawName,
            description: item.category_name || '',
            amount: usdAmount,
            maxAmount: item.max_price ? parseFloat(item.metadata?.price?.operator_max || item.max_price) : null,
            priceType: item.price_type || 'fixed',
            category: item.category_name || 'eVouchers',
            currencyCode: 'USD', // Always USD for display
            inStock: true,
            metadata: item.metadata || null,
            ngnPrice: item.price, // Keep NGN price for payment
          };
        });

        setProducts(transformedProducts);
      } catch (error) {
        console.error('[GifftinsStore] Error fetching products:', error);
        setProducts([]);
      } finally {
        setProductsLoading(false);
      }
    };

    fetchProducts();
  }, [selectedProvider]);

  // Default to USD for all gift card displays
  const displayCurrency = 'USD';

  // Filter providers by country and search (show USD products by default)
  const filteredProviders = useMemo(() => {
    if (!selectedCountry) return [];
    let filtered = allProviders.filter(p => p.country === selectedCountry);

    // Always filter to show USD providers
    filtered = filtered.filter(p => p.currency === 'USD');

    if (providerSearch) {
      const search = providerSearch.toLowerCase();
      filtered = filtered.filter(p => p.name.toLowerCase().includes(search));
    }
    return filtered;
  }, [allProviders, selectedCountry, providerSearch]);

  // Filter products to show USD only
  const filteredProducts = useMemo(() => {
    return products.filter(p => p.currencyCode === 'USD');
  }, [products]);

  // Get recipient's local currency for conversion display
  const recipientLocalCurrency = useMemo(() => {
    return getCountryCurrency(recipientCountry);
  }, [recipientCountry]);

  // Filter countries by search
  const filteredCountries = useMemo(() => {
    if (!countrySearch) return countries;
    const search = countrySearch.toLowerCase();
    return countries.filter(c => c.toLowerCase().includes(search));
  }, [countries, countrySearch]);

  // Flutter auth listener
  useEffect(() => {
    if (localStorage.getItem(FLUTTER_STORAGE_KEYS.FLUTTER_INJECTED) === 'true') {
      setFlutterAuthReady(true);
    }
    const handleFlutterAuth = () => setFlutterAuthReady(true);
    window.addEventListener('bonndleAuthInjected', handleFlutterAuth);
    return () => window.removeEventListener('bonndleAuthInjected', handleFlutterAuth);
  }, []);

  // Fetch payment methods
  useEffect(() => {
    const fetchPaymentMethods = async () => {
      setCardsLoading(true);
      try {
        const flutterRewards = localStorage.getItem(FLUTTER_STORAGE_KEYS.REWARDS_BALANCE);
        const flutterCards = localStorage.getItem(FLUTTER_STORAGE_KEYS.SAVED_CARDS);

        if (flutterRewards) setRewardsBalance(parseFloat(flutterRewards) || 0);
        if (flutterCards) {
          try {
            const cards = JSON.parse(flutterCards);
            if (Array.isArray(cards)) setSavedCards(cards);
          } catch (e) {}
        }

        const hasToken = !!getAuthToken();
        const [cardsResult, , profileResult] = await Promise.allSettled([
          hasToken ? bonndleAuthApi.getCards() : Promise.resolve([]),
          defaultsApi.get(),
          hasToken ? bonndleAuthApi.getProfile().catch(() => null) : Promise.resolve(null),
        ]);

        if (!flutterCards && cardsResult.status === 'fulfilled') {
          setSavedCards(Array.isArray(cardsResult.value) ? cardsResult.value : []);
        }

        if (!flutterRewards) {
          let balance = 0;
          if (profileResult.status === 'fulfilled' && profileResult.value) {
            balance = profileResult.value.available_bold_credits || 0;
          } else if (user) {
            balance = user.available_bold_credits || 0;
          }
          setRewardsBalance(balance);
        }
      } catch (err) {
        console.error('Failed to fetch payment methods:', err);
      } finally {
        setCardsLoading(false);
      }
    };
    fetchPaymentMethods();
  }, [flutterAuthReady, user]);

  // Navigation handlers
  const handleBack = () => {
    if (selectedProduct) {
      setSelectedProduct(null);
      resetGiftForm();
    } else if (selectedProvider) {
      setSelectedProvider(null);
      setProducts([]);
    } else if (selectedCountry) {
      setSelectedCountry(null);
      setProviderSearch('');
    } else {
      navigate(-1);
    }
  };

  const resetGiftForm = () => {
    setGiftAmount('');
    setRecipientCountry(userCountry);
    setRecipientName('');
    setRecipientEmail('');
    setGiftNote('');
  };

  const handleProductSelect = (product) => {
    setSelectedProduct(product);
    // Pre-fill amount for fixed products
    if (product.priceType !== 'range') {
      setGiftAmount(product.amount.toString());
    } else {
      setGiftAmount('');
    }
    resetGiftForm();
    if (product.priceType !== 'range') {
      setGiftAmount(product.amount.toString());
    }
  };

  const handleAddGiftToCart = () => {
    if (!selectedProduct) return;

    const amount = parseFloat(giftAmount);
    if (isNaN(amount) || amount <= 0) {
      alert('Please enter a valid amount');
      return;
    }

    // Validate range
    if (selectedProduct.priceType === 'range') {
      if (amount < selectedProduct.amount || amount > selectedProduct.maxAmount) {
        alert(`Amount must be between ${formatCurrency(selectedProduct.amount, selectedProduct.currencyCode)} and ${formatCurrency(selectedProduct.maxAmount, selectedProduct.currencyCode)}`);
        return;
      }
    }

    if (!recipientCountry) {
      alert('Please select recipient\'s country');
      return;
    }

    if (!recipientName.trim()) {
      alert('Please enter recipient name');
      return;
    }

    if (!recipientEmail.trim() || !recipientEmail.includes('@')) {
      alert('Please enter a valid recipient email');
      return;
    }

    // Show confirmation modal
    setShowGiftConfirmation(true);
  };

  const confirmAddToCart = () => {
    const localCurrency = getCountryCurrency(recipientCountry);
    const localAmount = convertCurrency(parseFloat(giftAmount), 'USD', localCurrency);

    const giftItem = {
      ...selectedProduct,
      amount: parseFloat(giftAmount),
      recipientCountry: recipientCountry,
      recipientName: recipientName.trim(),
      recipientEmail: recipientEmail.trim(),
      giftNote: giftNote.trim(),
      providerName: selectedProvider?.name?.replace(/^[A-Za-z]+\s/, '') || 'Gift Card',
      localCurrency: localCurrency,
      localAmount: localAmount,
    };

    setCart((prev) => [...prev, { product: giftItem, quantity: 1 }]);
    setShowGiftConfirmation(false);
    setSelectedProduct(null);
    resetGiftForm();
  };

  const handleCountrySelect = (country) => {
    setSelectedCountry(country);
    setSelectedProvider(null);
    setProviderSearch('');
    setShowCountryDropdown(false);
  };

  const handleProviderSelect = (provider) => {
    setSelectedProvider(provider);
  };

  // Cart functions
  const addToCart = (product) => {
    setCart((prev) => {
      const existing = prev.find((item) => item.product.id === product.id);
      if (existing) {
        return prev.map((item) =>
          item.product.id === product.id ? { ...item, quantity: item.quantity + 1 } : item
        );
      }
      return [...prev, { product, quantity: 1 }];
    });
  };

  const removeFromCart = (productId) => {
    setCart((prev) => prev.filter((item) => item.product.id !== productId));
  };

  const updateQuantity = (productId, delta) => {
    setCart((prev) =>
      prev.map((item) =>
        item.product.id === productId ? { ...item, quantity: Math.max(0, item.quantity + delta) } : item
      ).filter((item) => item.quantity > 0)
    );
  };

  const cartTotal = cart.reduce((sum, item) => sum + item.product.amount * item.quantity, 0);
  const cartItemCount = cart.reduce((sum, item) => sum + item.quantity, 0);
  const cashbackPercent = appliedCashback?.percentage || 0;
  const cashbackAmount = (cartTotal * cashbackPercent) / 100;
  const finalTotal = cartTotal;

  // Payment functions
  const generatePaymentReference = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    return 'GF' + Array.from({ length: 8 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
  };

  const handleAddNewCard = async () => {
    if (addingCard) return;
    setAddingCard(true);
    try {
      const initResponse = await paymentApi.initializePayment({
        amount: 100,
        paymentReference: generatePaymentReference(),
        currencyCode: 'NGN',
        channel: 'card',
      });
      const paymentUrl = initResponse.url || initResponse.authorizationUrl || initResponse.data?.url;
      if (paymentUrl) {
        const popup = window.open(paymentUrl, 'paystack_popup', 'width=500,height=600');
        const pollTimer = setInterval(() => {
          if (popup?.closed) {
            clearInterval(pollTimer);
            setTimeout(async () => {
              const cards = await bonndleAuthApi.getCards();
              if (Array.isArray(cards)) setSavedCards(cards);
              setAddingCard(false);
            }, 1000);
          }
        }, 500);
        setTimeout(() => { clearInterval(pollTimer); setAddingCard(false); }, 300000);
      }
    } catch (err) {
      alert('Failed to add card.');
      setAddingCard(false);
    }
  };

  const validateCashbackCode = async () => {
    if (!cashbackCode.trim()) return;
    setValidatingCode(true);
    setValidationError(null);
    try {
      const token = getUserToken(authTokenFromUrl);
      if (!token) throw new Error('Please log in to apply cashback codes.');
      const response = await fetch(`${API_BASE}/group-code/validate`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ discountCode: cashbackCode.trim() }),
      });
      if (!response.ok) throw new Error('Invalid cashback code');
      const data = await response.json();
      setAppliedCashback({
        code: cashbackCode,
        percentage: data.discount || data.discountPercentage || 10,
        message: data.message || 'Applied!',
      });
    } catch (error) {
      setValidationError(error.message);
    } finally {
      setValidatingCode(false);
    }
  };

  const processPayment = async () => {
    if (!selectedPayment) return alert('Please select a payment method');
    setProcessingPayment(true);
    try {
      await new Promise((r) => setTimeout(r, 2000));
      const transactionId = `GF_${Date.now()}_${Math.random().toString(36).substr(2, 9).toUpperCase()}`;
      setTransactionDetails({
        transactionId,
        amount: finalTotal,
        cashbackEarned: cashbackAmount,
        items: cart,
        paymentMethod: selectedPayment,
      });
      setCart([]);
      setShowPayment(false);
      setShowSuccess(true);
      setAppliedCashback(null);
      setSelectedPayment(null);
    } catch (error) {
      alert('Payment failed.');
    } finally {
      setProcessingPayment(false);
    }
  };

  const isFromFlutter = isEmbeddedFlutter || flutterAuthReady;

  return (
    <div className="min-h-screen" style={{ backgroundColor: THEME.canvas }}>
      {/* Header */}
      {!isFromFlutter && (
        <header className="sticky top-0 z-40 bg-white border-b" style={{ borderColor: THEME.lilac }}>
          <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <button onClick={handleBack} className="p-2 -ml-2 rounded-full hover:bg-purple-50">
                <ChevronLeft className="w-5 h-5 text-purple-700" />
              </button>
              <img
                src={GIFFTINS_LOGO}
                alt="Gifftins"
                className="w-10 h-10 rounded-xl object-contain"
              />
              <h1 className="font-semibold text-lg text-purple-900" style={{ marginLeft: '5px' }}>Gifftins</h1>
            </div>

            <div className="flex items-center gap-2">
              {/* Country dropdown - always show with detected/selected country */}
              {(selectedCountry || detectedCountry) && (
                <div className="relative" ref={countryDropdownRef}>
                  <button
                    onClick={() => setShowCountryDropdown(!showCountryDropdown)}
                    className="flex items-center gap-1.5 px-2 py-1.5 bg-purple-50 hover:bg-purple-100 rounded-lg"
                  >
                    <span className="text-xl">{getCountryFlag(getCountryCode(selectedCountry || detectedCountry))}</span>
                    <ChevronDown className={`w-4 h-4 text-purple-500 transition-transform ${showCountryDropdown ? 'rotate-180' : ''}`} />
                  </button>
                  {showCountryDropdown && (
                    <div className="absolute right-0 top-full mt-2 w-64 bg-white rounded-xl shadow-xl border overflow-hidden z-50">
                      <div className="p-2 border-b">
                        <div className="relative">
                          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                          <input
                            type="text"
                            placeholder="Search..."
                            value={countrySearch}
                            onChange={(e) => setCountrySearch(e.target.value)}
                            className="w-full pl-8 pr-3 py-2 bg-gray-50 rounded-lg text-sm focus:outline-none"
                          />
                        </div>
                      </div>
                      <div className="max-h-64 overflow-y-auto">
                        {filteredCountries.map((country) => (
                          <button
                            key={country}
                            onClick={() => handleCountrySelect(country)}
                            className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-left hover:bg-purple-50 ${country === selectedCountry ? 'bg-purple-50' : ''}`}
                          >
                            <span className="text-xl">{getCountryFlag(getCountryCode(country))}</span>
                            <span className="flex-1 text-sm font-medium">{country}</span>
                            {country === selectedCountry && <Check className="w-4 h-4 text-purple-600" />}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <button onClick={() => setShowCart(true)} className="relative p-2 hover:bg-purple-50 rounded-full">
                <ShoppingCart className="w-6 h-6 text-purple-700" />
                {cartItemCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 text-white text-xs rounded-full flex items-center justify-center" style={{ backgroundColor: THEME.gold }}>
                    {cartItemCount}
                  </span>
                )}
              </button>
            </div>
          </div>
        </header>
      )}

      {/* Hero Section - Show on home page (no provider selected) */}
      {!selectedProvider && !loadingProviders && !detectingLocation && (
        <section className="relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${THEME.primary} 0%, ${THEME.primaryDark} 100%)` }}>
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-10 left-10 w-32 h-32 rounded-full" style={{ backgroundColor: THEME.gold }} />
            <div className="absolute bottom-10 right-10 w-48 h-48 rounded-full" style={{ backgroundColor: THEME.goldLight }} />
          </div>
          <div className="relative max-w-7xl mx-auto px-4 py-10">
            <div className="text-center">
              {/* Logo in hero */}
              <img
                src={GIFFTINS_LOGO}
                alt="Gifftins"
                className="w-16 h-16 mx-auto mb-3 rounded-2xl object-contain bg-white/20 p-2"
              />
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-4" style={{ backgroundColor: 'rgba(255,255,255,0.15)' }}>
                <Sparkles className="w-4 h-4 text-amber-300" />
                <span className="text-sm text-white font-medium">Premium Gift Cards</span>
              </div>
              <h1 className="text-2xl md:text-3xl font-bold text-white mb-3">
                The Perfect Gift for
                <span className="block mt-1" style={{ color: THEME.goldLight }}>Every Occasion</span>
              </h1>
              <p className="text-purple-100 max-w-xl mx-auto mb-6">
                Send gift cards instantly to loved ones worldwide.
              </p>
            </div>
          </div>
        </section>
      )}

      {/* Search Bar Section - Show on home page */}
      {!selectedProvider && !loadingProviders && !detectingLocation && (
        <div className="max-w-7xl mx-auto px-4 -mt-6 relative z-10">
          <button
            onClick={() => {
              // If no country selected, auto-select the detected country first
              if (!selectedCountry && detectedCountry) {
                const countriesWithUSD = countries.filter(c =>
                  allProviders.some(p => p.country === c && p.currency === 'USD')
                );
                const matchedCountry = countriesWithUSD.find(c =>
                  c.toLowerCase() === detectedCountry.toLowerCase() ||
                  c.toLowerCase().includes(detectedCountry.toLowerCase()) ||
                  detectedCountry.toLowerCase().includes(c.toLowerCase())
                );
                if (matchedCountry) {
                  setSelectedCountry(matchedCountry);
                }
              }
            }}
            className="w-full flex items-center gap-3 p-4 bg-white rounded-2xl shadow-lg border border-purple-100 hover:shadow-xl transition-all"
          >
            <Search className="w-5 h-5 text-purple-400" />
            <span className="flex-1 text-left text-purple-400">Search for gift cards...</span>
            <ArrowRight className="w-5 h-5 text-purple-400" />
          </button>
        </div>
      )}

      {/* Use Cases Section - Show on home page */}
      {!selectedProvider && !loadingProviders && !detectingLocation && (
        <div className="max-w-7xl mx-auto px-4 py-8">
          <h2 className="text-lg font-bold text-purple-900 mb-4">Gift Cards for Everyone</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Individuals */}
            <div className="bg-white rounded-2xl p-5 border border-purple-100 hover:shadow-md transition-all">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-3" style={{ backgroundColor: '#EDE9FE' }}>
                <Gift className="w-6 h-6" style={{ color: THEME.primary }} />
              </div>
              <h3 className="font-semibold text-purple-900 mb-2">For Individuals</h3>
              <p className="text-sm text-purple-500">Send personalized gift cards to friends and family for birthdays, holidays, or just because.</p>
            </div>

            {/* Groups */}
            <div className="bg-white rounded-2xl p-5 border border-purple-100 hover:shadow-md transition-all">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-3" style={{ backgroundColor: '#FEF3C7' }}>
                <Users className="w-6 h-6" style={{ color: THEME.gold }} />
              </div>
              <h3 className="font-semibold text-purple-900 mb-2">For Groups</h3>
              <p className="text-sm text-purple-500">Pool funds with friends or family to gift larger amounts together for special occasions.</p>
            </div>

            {/* Teams */}
            <div className="bg-white rounded-2xl p-5 border border-purple-100 hover:shadow-md transition-all">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-3" style={{ backgroundColor: '#DBEAFE' }}>
                <Building2 className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="font-semibold text-purple-900 mb-2">For Teams</h3>
              <p className="text-sm text-purple-500">Reward employees, celebrate milestones, or send bulk gift cards for corporate events.</p>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        {loadingProviders || detectingLocation ? (
          <div className="flex flex-col items-center justify-center py-20">
            <Loader2 className="w-10 h-10 animate-spin mb-4" style={{ color: THEME.primary }} />
            <p className="text-purple-500">
              {detectingLocation ? 'Detecting your location...' : 'Loading gift cards...'}
            </p>
            {detectingLocation && (
              <p className="text-sm mt-2 text-purple-300">This will only take a moment</p>
            )}
          </div>
        ) : !selectedProvider && !selectedCountry ? (
          /* Home Page - Show popular brands */
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold text-purple-900">Popular Gift Cards</h2>
              <button
                onClick={() => {
                  if (!selectedCountry && detectedCountry) {
                    const countriesWithUSD = countries.filter(c =>
                      allProviders.some(p => p.country === c && p.currency === 'USD')
                    );
                    const matchedCountry = countriesWithUSD.find(c =>
                      c.toLowerCase() === detectedCountry.toLowerCase() ||
                      c.toLowerCase().includes(detectedCountry.toLowerCase()) ||
                      detectedCountry.toLowerCase().includes(c.toLowerCase())
                    );
                    if (matchedCountry) {
                      setSelectedCountry(matchedCountry);
                    }
                  }
                }}
                className="text-sm font-medium text-purple-600 hover:text-purple-800"
              >
                View All →
              </button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {filteredProviders.slice(0, 8).map((provider) => (
                <button
                  key={provider.provider_uuid}
                  onClick={() => handleProviderSelect(provider)}
                  className="flex flex-col items-center gap-2 p-4 bg-white rounded-xl border border-purple-100 hover:shadow-md hover:border-purple-300 transition-all"
                >
                  <div className="w-14 h-14 rounded-xl flex items-center justify-center" style={{ backgroundColor: THEME.creamDark }}>
                    <span className="font-bold text-xl" style={{ color: THEME.primary }}>
                      {provider.name.replace(/^[A-Za-z]+\s/, '').charAt(0)}
                    </span>
                  </div>
                  <span className="text-sm font-medium text-purple-900 text-center line-clamp-2">
                    {provider.name.replace(/^[A-Za-z]+\s/, '')}
                  </span>
                </button>
              ))}
            </div>
          </div>
        ) : !selectedCountry ? (
          /* Step 1: Country Selection */
          <div className="space-y-4">
            <div className="text-center py-4">
              <h2 className="text-xl font-bold mb-2" style={{ color: THEME.ink }}>Select Your Country</h2>
              <p className="text-sm" style={{ color: THEME.primaryLight }}>{countries.length} countries with gift cards</p>
            </div>

            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-purple-400" />
              <input
                type="text"
                placeholder="Search countries..."
                value={countrySearch}
                onChange={(e) => setCountrySearch(e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-white border border-purple-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>

            <div className="grid grid-cols-1 gap-2">
              {filteredCountries.map((country) => {
                // Count USD providers only
                const providerCount = allProviders.filter(p =>
                  p.country === country && p.currency === 'USD'
                ).length;
                if (providerCount === 0) return null; // Hide countries with no USD gift cards
                return (
                  <button
                    key={country}
                    onClick={() => handleCountrySelect(country)}
                    className="flex items-center gap-3 p-4 bg-white rounded-xl border border-purple-100 hover:border-purple-300 hover:shadow-md transition-all"
                  >
                    <span className="text-2xl">{getCountryFlag(getCountryCode(country))}</span>
                    <div className="flex-1 text-left">
                      <span className="font-medium text-purple-900">{country}</span>
                      <p className="text-xs text-purple-500">{providerCount} gift cards (USD)</p>
                    </div>
                    <ChevronDown className="w-5 h-5 text-purple-400 -rotate-90" />
                  </button>
                );
              })}
            </div>
          </div>
        ) : !selectedProvider ? (
          /* Step 2: Provider Selection */
          <div className="space-y-4">
            <div className="flex items-center gap-3 p-4 bg-purple-50 rounded-xl border border-purple-100">
              <span className="text-3xl">{getCountryFlag(getCountryCode(selectedCountry))}</span>
              <div className="flex-1">
                <p className="font-bold text-purple-900">{selectedCountry}</p>
                <p className="text-xs text-purple-600">
                  {filteredProviders.length} gift cards available (USD)
                </p>
              </div>
              <button onClick={() => setSelectedCountry(null)} className="p-2 hover:bg-purple-100 rounded-full">
                <X className="w-4 h-4 text-purple-600" />
              </button>
            </div>

            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-purple-400" />
              <input
                type="text"
                placeholder="Search gift cards..."
                value={providerSearch}
                onChange={(e) => setProviderSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-white border border-purple-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {filteredProviders.map((provider) => (
                <button
                  key={provider.provider_uuid}
                  onClick={() => handleProviderSelect(provider)}
                  className="flex flex-col items-center gap-3 p-4 bg-white rounded-xl border border-purple-100 hover:border-purple-300 hover:shadow-md transition-all"
                >
                  {provider.logo_url ? (
                    <img
                      src={provider.logo_url}
                      alt={provider.name}
                      className="w-14 h-14 rounded-xl object-contain"
                      style={{ backgroundColor: THEME.creamDark }}
                      onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                    />
                  ) : null}
                  <div
                    className={`w-14 h-14 rounded-xl items-center justify-center ${provider.logo_url ? 'hidden' : 'flex'}`}
                    style={{ backgroundColor: THEME.creamDark }}
                  >
                    <span className="font-bold text-xl" style={{ color: THEME.primary }}>
                      {provider.name.replace(/^[A-Za-z]+\s/, '').charAt(0)}
                    </span>
                  </div>
                  <div className="text-center">
                    <p className="font-medium text-purple-900 text-sm line-clamp-2">
                      {provider.name.replace(/^[A-Za-z]+\s/, '')}
                    </p>
                    <p className="text-xs text-purple-500 mt-1">{provider.currency}</p>
                  </div>
                </button>
              ))}
            </div>

            {filteredProviders.length === 0 && (
              <div className="text-center py-12 text-purple-500">
                <p className="font-medium">No gift cards found</p>
                {providerSearch && <p className="text-sm mt-1">Try a different search</p>}
              </div>
            )}
          </div>
        ) : (
          /* Step 3: Products for selected provider */
          <div className="space-y-4">
            <div className="flex items-center gap-3 p-4 bg-white rounded-xl border border-purple-100">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: THEME.creamDark }}>
                <span className="font-bold text-lg" style={{ color: THEME.primary }}>
                  {selectedProvider.name.replace(/^[A-Za-z]+\s/, '').charAt(0)}
                </span>
              </div>
              <div className="flex-1">
                <p className="font-bold text-purple-900">{selectedProvider.name.replace(/^[A-Za-z]+\s/, '')}</p>
                <p className="text-xs text-purple-500">{selectedProvider.country} • {selectedProvider.currency}</p>
              </div>
              <button onClick={() => setSelectedProvider(null)} className="p-2 hover:bg-purple-50 rounded-full">
                <X className="w-4 h-4 text-purple-400" />
              </button>
            </div>

            {productsLoading ? (
              <div className="flex flex-col items-center justify-center py-16">
                <Loader2 className="w-8 h-8 animate-spin mb-3" style={{ color: THEME.primary }} />
                <p className="text-purple-500 text-sm">Loading products...</p>
              </div>
            ) : filteredProducts.length === 0 ? (
              <div className="text-center py-16 text-purple-500">
                <Package className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p className="font-medium">No products available</p>
                {products.length > 0 && (
                  <p className="text-sm mt-1">No USD products for this provider</p>
                )}
              </div>
            ) : !selectedProduct ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {filteredProducts.map((product) => (
                  <ProductCard
                    key={product.id}
                    product={product}
                    onSelect={() => handleProductSelect(product)}
                    theme={THEME}
                  />
                ))}
              </div>
            ) : (
              /* Product Detail Screen */
              <ProductDetailScreen
                product={selectedProduct}
                provider={selectedProvider}
                recipientCountry={recipientCountry}
                setRecipientCountry={setRecipientCountry}
                recipientName={recipientName}
                setRecipientName={setRecipientName}
                recipientEmail={recipientEmail}
                setRecipientEmail={setRecipientEmail}
                giftAmount={giftAmount}
                setGiftAmount={setGiftAmount}
                giftNote={giftNote}
                setGiftNote={setGiftNote}
                recipientLocalCurrency={recipientLocalCurrency}
                onAddToCart={handleAddGiftToCart}
                onBack={() => { setSelectedProduct(null); resetGiftForm(); }}
                theme={THEME}
              />
            )}
          </div>
        )}
      </main>

      {/* Cart Drawer */}
      {showCart && (
        <CartDrawer
          cart={cart}
          cartTotal={cartTotal}
          cartItemCount={cartItemCount}
          cashbackCode={cashbackCode}
          setCashbackCode={setCashbackCode}
          appliedCashback={appliedCashback}
          cashbackAmount={cashbackAmount}
          finalTotal={finalTotal}
          validatingCode={validatingCode}
          validationError={validationError}
          onValidateCode={validateCashbackCode}
          onUpdateQuantity={updateQuantity}
          onRemoveFromCart={removeFromCart}
          onCheckout={() => setShowPayment(true)}
          onClose={() => setShowCart(false)}
          theme={THEME}
        />
      )}

      {/* Payment Modal */}
      {showPayment && (
        <PaymentModal
          finalTotal={finalTotal}
          rewardsBalance={rewardsBalance}
          rewardsInUSD={rewardsInUSD}
          savedCards={savedCards}
          cardsLoading={cardsLoading}
          selectedPayment={selectedPayment}
          setSelectedPayment={setSelectedPayment}
          addingCard={addingCard}
          onAddNewCard={handleAddNewCard}
          cashbackAmount={cashbackAmount}
          onProceed={() => { setShowPayment(false); setShowCheckout(true); }}
          onClose={() => setShowPayment(false)}
          theme={THEME}
        />
      )}

      {/* Checkout Modal */}
      {showCheckout && (
        <CheckoutModal
          cart={cart}
          cartTotal={cartTotal}
          appliedCashback={appliedCashback}
          cashbackAmount={cashbackAmount}
          finalTotal={finalTotal}
          selectedPayment={selectedPayment}
          processingPayment={processingPayment}
          onProcess={processPayment}
          onBack={() => { setShowCheckout(false); setShowPayment(true); }}
          onClose={() => setShowCheckout(false)}
          theme={THEME}
        />
      )}

      {/* Success Modal */}
      {showSuccess && transactionDetails && (
        <SuccessModal
          transactionDetails={transactionDetails}
          onClose={() => { setShowSuccess(false); setTransactionDetails(null); setShowCart(false); }}
          theme={THEME}
        />
      )}

      {/* Gift Confirmation Modal */}
      {showGiftConfirmation && selectedProduct && (
        <GiftConfirmationModal
          product={selectedProduct}
          provider={selectedProvider}
          amount={parseFloat(giftAmount)}
          recipientCountry={recipientCountry}
          recipientName={recipientName}
          recipientEmail={recipientEmail}
          giftNote={giftNote}
          recipientLocalCurrency={recipientLocalCurrency}
          onConfirm={confirmAddToCart}
          onClose={() => setShowGiftConfirmation(false)}
          theme={THEME}
        />
      )}

      {/* Floating Cart Button */}
      {cartItemCount > 0 && !showCart && !showCheckout && !showPayment && !showSuccess && (
        <button
          onClick={() => setShowCart(true)}
          className="fixed bottom-6 right-6 z-30 text-white px-6 py-3 rounded-full shadow-lg flex items-center gap-2 md:hidden"
          style={{ backgroundColor: THEME.primary }}
        >
          <ShoppingCart className="w-5 h-5" />
          <span className="font-medium">{formatCurrency(cartTotal)}</span>
        </button>
      )}
    </div>
  );
}

// Product Card Component
function ProductCard({ product, onSelect, theme }) {
  const isRangePrice = product.priceType === 'range' && product.maxAmount;

  return (
    <button
      onClick={onSelect}
      className="flex items-center gap-4 p-4 bg-white rounded-xl border border-purple-100 hover:shadow-md hover:border-purple-300 transition-all text-left w-full"
    >
      <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#EDE9FE' }}>
        <Gift className="w-5 h-5" style={{ color: theme.primary }} />
      </div>
      <div className="flex-1">
        <span className="font-semibold text-base" style={{ color: theme.primary }}>
          {isRangePrice ? (
            <>${product.amount} - ${product.maxAmount} USD</>
          ) : (
            <>{product.title}</>
          )}
        </span>
      </div>
      <ArrowRight className="w-5 h-5 text-purple-400" />
    </button>
  );
}

// Product Detail Screen Component
function ProductDetailScreen({ product, provider, recipientCountry, setRecipientCountry, recipientName, setRecipientName, recipientEmail, setRecipientEmail, giftAmount, setGiftAmount, giftNote, setGiftNote, recipientLocalCurrency, onAddToCart, onBack, theme }) {
  const isRangePrice = product.priceType === 'range' && product.maxAmount;
  const providerName = provider?.name?.replace(/^[A-Za-z]+\s/, '') || 'Gift Card';
  const displayAmount = giftAmount ? parseFloat(giftAmount) : product.amount;

  // Calculate local currency equivalent
  const localAmount = giftAmount && recipientLocalCurrency && recipientLocalCurrency !== 'USD'
    ? convertCurrency(parseFloat(giftAmount), 'USD', recipientLocalCurrency)
    : null;

  return (
    <div className="space-y-6">
      {/* Hero Gift Card */}
      <div className="relative overflow-hidden rounded-2xl" style={{ background: `linear-gradient(135deg, ${theme.primary} 0%, ${theme.primaryDark} 100%)` }}>
        {/* Decorative ribbon */}
        <div className="absolute top-0 right-0 w-24 h-24">
          <div className="absolute transform rotate-45 translate-x-8 -translate-y-4 w-32 h-12" style={{ backgroundColor: theme.gold }} />
        </div>
        <div className="absolute bottom-0 left-0 w-20 h-20">
          <div className="absolute transform -rotate-45 -translate-x-6 translate-y-6 w-28 h-10" style={{ backgroundColor: theme.goldLight, opacity: 0.5 }} />
        </div>

        <div className="relative p-6 py-10 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4" style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}>
            <Gift className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">{providerName}</h2>
          <div className="inline-flex flex-col items-center gap-1 px-4 py-2 rounded-full" style={{ backgroundColor: 'rgba(255,255,255,0.15)' }}>
            <span className="text-xl font-bold" style={{ color: theme.goldLight }}>
              {giftAmount ? formatCurrency(parseFloat(giftAmount), 'USD') : (
                isRangePrice ? `${formatCurrency(product.amount, 'USD')} - ${formatCurrency(product.maxAmount, 'USD')}` : formatCurrency(product.amount, 'USD')
              )}
            </span>
          </div>
          {/* Show local currency equivalent below badge if recipient country is not US */}
          {recipientCountry && recipientCountry !== 'United States' && recipientCountry !== 'United States of America' && recipientLocalCurrency && recipientLocalCurrency !== 'USD' && (
            <p className="text-purple-200 text-sm mt-3">
              ≈ {formatLocalCurrency(
                convertCurrency(giftAmount ? parseFloat(giftAmount) : product.amount, 'USD', recipientLocalCurrency),
                recipientLocalCurrency
              )}
            </p>
          )}
        </div>
      </div>

      {/* Gift Form */}
      <div className="bg-white rounded-xl p-5 space-y-4 border border-purple-100">
        <h3 className="font-semibold text-purple-900 flex items-center gap-2">
          <Gift className="w-5 h-5" style={{ color: theme.primary }} />
          Gift Details
        </h3>

        {/* Recipient's Country */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-purple-700">Recipient's Country *</label>
          <div className="relative">
            <Globe className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-purple-400" />
            <select
              value={recipientCountry}
              onChange={(e) => setRecipientCountry(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-gray-50 border border-purple-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 appearance-none"
            >
              <option value="">Select country</option>
              {RECIPIENT_COUNTRIES.map((country) => (
                <option key={country} value={country}>
                  {country}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-purple-400 pointer-events-none" />
          </div>
        </div>

        {/* Recipient Name */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-purple-700">Recipient's Name *</label>
          <input
            type="text"
            value={recipientName}
            onChange={(e) => setRecipientName(e.target.value)}
            placeholder="Enter recipient's name"
            className="w-full px-4 py-3 bg-gray-50 border border-purple-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
          />
        </div>

        {/* Recipient Email */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-purple-700">Recipient's Email *</label>
          <input
            type="email"
            value={recipientEmail}
            onChange={(e) => setRecipientEmail(e.target.value)}
            placeholder="Enter recipient's email"
            className="w-full px-4 py-3 bg-gray-50 border border-purple-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
          />
          <p className="text-xs text-purple-400">Gift card will be sent to this email</p>
        </div>

        {/* Amount Input */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-purple-700">Amount (USD) *</label>
          {isRangePrice ? (
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-purple-400 font-medium">$</span>
              <input
                type="number"
                value={giftAmount}
                onChange={(e) => setGiftAmount(e.target.value)}
                placeholder={`${product.amount} - ${product.maxAmount}`}
                min={product.amount}
                max={product.maxAmount}
                className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-purple-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 text-lg font-semibold"
                style={{ color: theme.primary }}
              />
              <div className="flex justify-between mt-1">
                <p className="text-xs text-purple-400">
                  Min: ${product.amount} • Max: ${product.maxAmount}
                </p>
                {/* Show local currency equivalent */}
                {localAmount && recipientLocalCurrency !== 'USD' && (
                  <p className="text-xs font-medium" style={{ color: theme.primary }}>
                    ≈ {formatLocalCurrency(localAmount, recipientLocalCurrency)}
                  </p>
                )}
              </div>
            </div>
          ) : (
            <div className="px-4 py-3 bg-gray-50 border border-purple-200 rounded-xl">
              <div className="flex justify-between items-center">
                <span className="text-lg font-semibold" style={{ color: theme.primary }}>
                  {formatCurrency(product.amount, 'USD')}
                </span>
                {/* Show local currency equivalent for fixed amounts */}
                {recipientLocalCurrency && recipientLocalCurrency !== 'USD' && (
                  <span className="text-sm font-medium" style={{ color: theme.primary }}>
                    ≈ {formatLocalCurrency(convertCurrency(product.amount, 'USD', recipientLocalCurrency), recipientLocalCurrency)}
                  </span>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Gift Note */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-purple-700">Gift Note (Optional)</label>
          <textarea
            value={giftNote}
            onChange={(e) => setGiftNote(e.target.value)}
            placeholder="Add a personal message..."
            rows={3}
            className="w-full px-4 py-3 bg-gray-50 border border-purple-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
          />
        </div>
      </div>

      {/* Add to Cart Button */}
      <button
        onClick={onAddToCart}
        className="w-full py-4 text-white rounded-xl font-semibold text-lg flex items-center justify-center gap-2 shadow-lg"
        style={{ backgroundColor: theme.primary }}
      >
        <ShoppingCart className="w-5 h-5" />
        Add to Cart
      </button>
    </div>
  );
}

// Gift Confirmation Modal
function GiftConfirmationModal({ product, provider, amount, recipientCountry, recipientName, recipientEmail, giftNote, recipientLocalCurrency, onConfirm, onClose, theme }) {
  const providerName = provider?.name?.replace(/^[A-Za-z]+\s/, '') || 'Gift Card';
  const localAmount = recipientLocalCurrency && recipientLocalCurrency !== 'USD'
    ? convertCurrency(amount, 'USD', recipientLocalCurrency)
    : null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl w-full max-w-md overflow-hidden">
        {/* Header with gift card preview */}
        <div className="p-6 text-center" style={{ background: `linear-gradient(135deg, ${theme.primary} 0%, ${theme.primaryDark} 100%)` }}>
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full mb-3" style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}>
            <Gift className="w-6 h-6 text-white" />
          </div>
          <h2 className="text-xl font-bold text-white">{providerName}</h2>
          <p className="text-2xl font-bold mt-2" style={{ color: theme.goldLight }}>
            {formatCurrency(amount, 'USD')}
          </p>
          {localAmount && recipientLocalCurrency !== 'USD' && (
            <p className="text-sm text-purple-200 mt-1">
              ≈ {formatLocalCurrency(localAmount, recipientLocalCurrency)}
            </p>
          )}
        </div>

        {/* Details */}
        <div className="p-6 space-y-4">
          <h3 className="font-semibold text-purple-900 text-center mb-4">Confirm Gift Details</h3>

          <div className="space-y-3 p-4 rounded-xl" style={{ backgroundColor: theme.creamDark }}>
            <div className="flex justify-between text-sm">
              <span className="text-purple-500">Country</span>
              <span className="font-medium text-purple-900">{recipientCountry}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-purple-500">Recipient</span>
              <span className="font-medium text-purple-900">{recipientName}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-purple-500">Email</span>
              <span className="font-medium text-purple-900">{recipientEmail}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-purple-500">Amount</span>
              <div className="text-right">
                <span className="font-semibold" style={{ color: theme.primary }}>{formatCurrency(amount, 'USD')}</span>
                {localAmount && recipientLocalCurrency !== 'USD' && (
                  <span className="text-xs text-purple-400 block">≈ {formatLocalCurrency(localAmount, recipientLocalCurrency)}</span>
                )}
              </div>
            </div>
            {giftNote && (
              <div className="pt-2 border-t border-purple-100">
                <span className="text-purple-500 text-sm block mb-1">Gift Note</span>
                <p className="text-sm text-purple-900 italic">"{giftNote}"</p>
              </div>
            )}
          </div>

          <div className="flex gap-3 pt-2">
            <button
              onClick={onClose}
              className="flex-1 py-3 border border-purple-300 rounded-xl font-medium text-purple-700"
            >
              Edit
            </button>
            <button
              onClick={onConfirm}
              className="flex-1 py-3 text-white rounded-xl font-semibold flex items-center justify-center gap-2"
              style={{ backgroundColor: theme.primary }}
            >
              <Check className="w-4 h-4" />
              Add to Cart
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Cart Drawer Component
function CartDrawer({ cart, cartTotal, cartItemCount, cashbackCode, setCashbackCode, appliedCashback, cashbackAmount, finalTotal, validatingCode, validationError, onValidateCode, onUpdateQuantity, onRemoveFromCart, onCheckout, onClose, theme }) {
  return (
    <div className="fixed inset-0 z-50 bg-white flex flex-col">
      <div className="p-4 border-b border-purple-100 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-purple-900">Your Cart ({cartItemCount})</h2>
        <button onClick={onClose} className="p-2 hover:bg-purple-50 rounded-full">
          <X className="w-5 h-5 text-purple-700" />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-4" style={{ backgroundColor: theme.cream }}>
        {cart.length === 0 ? (
          <div className="text-center py-12 text-purple-500">
            <ShoppingCart className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>Your cart is empty</p>
          </div>
        ) : (
          cart.map((item, index) => (
            <div key={`${item.product.id}-${index}`} className="p-4 bg-white rounded-xl border border-purple-100">
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: theme.creamDark }}>
                  <Gift className="w-6 h-6" style={{ color: theme.primary }} />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-sm text-purple-900">{item.product.providerName || item.product.title}</h3>
                  <div className="flex items-baseline gap-2">
                    <p className="text-lg font-bold" style={{ color: theme.primary }}>{formatCurrency(item.product.amount, 'USD')}</p>
                    {item.product.localAmount && item.product.localCurrency !== 'USD' && (
                      <span className="text-xs text-purple-400">≈ {formatLocalCurrency(item.product.localAmount, item.product.localCurrency)}</span>
                    )}
                  </div>
                  {item.product.recipientName && (
                    <div className="mt-2 pt-2 border-t border-purple-50 text-xs text-purple-500 space-y-1">
                      {item.product.recipientCountry && <p><span className="font-medium">Country:</span> {item.product.recipientCountry}</p>}
                      <p><span className="font-medium">To:</span> {item.product.recipientName}</p>
                      <p><span className="font-medium">Email:</span> {item.product.recipientEmail}</p>
                      {item.product.giftNote && <p className="italic">"{item.product.giftNote}"</p>}
                    </div>
                  )}
                </div>
                <button onClick={() => onRemoveFromCart(item.product.id)} className="p-1 text-purple-400 hover:text-red-500 flex-shrink-0">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
      {cart.length > 0 && (
        <div className="border-t border-purple-100 p-4 space-y-4 bg-white">
          <div className="flex gap-2">
            <input
              type="text"
              value={cashbackCode}
              onChange={(e) => setCashbackCode(e.target.value.toUpperCase())}
              placeholder="Cashback code"
              className="flex-1 px-4 py-3 border border-purple-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
            <button
              onClick={onValidateCode}
              disabled={!cashbackCode.trim() || appliedCashback || validatingCode}
              className="px-5 py-3 text-white rounded-lg text-sm font-medium disabled:opacity-50"
              style={{ backgroundColor: theme.primary }}
            >
              {validatingCode ? 'Applying...' : 'Apply'}
            </button>
          </div>
          {validationError && <p className="text-red-500 text-xs">{validationError}</p>}
          <div className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-purple-600">Subtotal</span><span>{formatCurrency(cartTotal)}</span></div>
            {appliedCashback && <div className="flex justify-between text-emerald-600"><span>Cashback ({appliedCashback.percentage}%)</span><span>+{formatCurrency(cashbackAmount)}</span></div>}
            <div className="flex justify-between font-semibold text-base pt-2 border-t"><span>Total</span><span>{formatCurrency(finalTotal)}</span></div>
          </div>
          <button onClick={onCheckout} className="w-full py-3 text-white rounded-xl font-semibold" style={{ backgroundColor: theme.primary }}>
            Proceed to Checkout
          </button>
        </div>
      )}
    </div>
  );
}

// Payment Modal Component
function PaymentModal({ finalTotal, rewardsBalance, rewardsInUSD, savedCards, cardsLoading, selectedPayment, setSelectedPayment, addingCard, onAddNewCard, cashbackAmount, onProceed, onClose, theme }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl w-full max-w-md p-6 space-y-4 max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-semibold text-purple-900">Select Payment Method</h2>
        <div className="p-3 rounded-lg" style={{ backgroundColor: theme.creamDark }}>
          <div className="flex justify-between text-sm">
            <span>Amount Due</span>
            <span className="font-semibold text-lg">{formatCurrency(finalTotal)}</span>
          </div>
        </div>
        <div className="space-y-3">
          <button
            onClick={() => rewardsBalance > 0 && setSelectedPayment({ id: 'rewards', type: 'rewards' })}
            disabled={rewardsBalance <= 0}
            className={`w-full p-4 rounded-xl border-2 text-left flex items-center gap-3 ${selectedPayment?.id === 'rewards' ? 'border-purple-600 bg-purple-50' : 'border-purple-100'} ${rewardsBalance <= 0 ? 'opacity-50' : ''}`}
          >
            <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center border">
              <img src="/logo/bonndleLogoIcon_Indigo.svg" alt="Rewards" className="w-8 h-8" />
            </div>
            <div className="flex-1">
              <p className="font-bold text-sm">Bonndle Rewards</p>
              <p className="text-xs text-purple-600">{Math.floor(rewardsBalance).toLocaleString()} Pts | ${rewardsInUSD.toFixed(2)}</p>
            </div>
            {selectedPayment?.id === 'rewards' && <Check className="w-5 h-5 text-purple-600" />}
          </button>
          {!cardsLoading && savedCards.map((card) => (
            <button
              key={card.uuid || card.id}
              onClick={() => setSelectedPayment(card)}
              className={`w-full p-4 rounded-xl border-2 text-left flex items-center gap-3 ${selectedPayment?.uuid === card.uuid ? 'border-purple-600 bg-purple-50' : 'border-purple-100'}`}
            >
              <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-purple-600" />
              </div>
              <div className="flex-1">
                <p className="font-bold text-sm">{card.cardProvider || 'Card'}</p>
                <p className="text-xs text-purple-500">•••• {card.last4Digits || card.last4}</p>
              </div>
              {selectedPayment?.uuid === card.uuid && <Check className="w-5 h-5 text-purple-600" />}
            </button>
          ))}
          {cardsLoading && <div className="flex justify-center py-4"><Loader2 className="w-6 h-6 animate-spin" style={{ color: theme.primary }} /></div>}
          {!cardsLoading && (
            <button onClick={onAddNewCard} disabled={addingCard} className="w-full p-3 rounded-xl border-2 border-dashed border-purple-200 text-purple-500 flex items-center justify-center gap-2">
              {addingCard ? <Loader2 className="w-5 h-5 animate-spin" /> : <Plus className="w-5 h-5" />}
              <span className="font-medium text-sm">{addingCard ? 'Adding...' : 'Add New Card'}</span>
            </button>
          )}
        </div>
        {selectedPayment && (
          <div className={`p-3 rounded-lg text-sm flex items-center gap-2 ${selectedPayment.id === 'rewards' ? 'bg-amber-50 text-amber-700' : 'bg-emerald-50 text-emerald-700'}`}>
            {selectedPayment.id === 'rewards' ? (
              <><Info className="w-4 h-4" /><span>No rewards earned on this purchase</span></>
            ) : (
              <><Coins className="w-4 h-4" /><span>Earn {formatCurrency(cashbackAmount)} cashback</span></>
            )}
          </div>
        )}
        <div className="flex gap-3 pt-2">
          <button onClick={onClose} className="flex-1 py-3 border border-purple-300 rounded-xl font-medium text-purple-700">Back</button>
          <button onClick={onProceed} disabled={!selectedPayment} className="flex-1 py-3 text-white rounded-xl font-semibold disabled:opacity-50 flex items-center justify-center gap-2" style={{ backgroundColor: theme.primary }}>
            Proceed <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

// Checkout Modal Component
function CheckoutModal({ cart, cartTotal, appliedCashback, cashbackAmount, finalTotal, selectedPayment, processingPayment, onProcess, onBack, onClose, theme }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl w-full max-w-md p-6 space-y-4 max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-semibold text-purple-900">Checkout Summary</h2>
        <div className="space-y-3">
          {cart.map((item, index) => (
            <div key={`${item.product.id}-${index}`} className="p-3 rounded-xl" style={{ backgroundColor: theme.creamDark }}>
              <div className="flex justify-between text-sm mb-1">
                <span className="font-medium text-purple-900">{item.product.providerName || item.product.title}</span>
                <span className="font-semibold" style={{ color: theme.primary }}>{formatCurrency(item.product.amount, item.product.currencyCode)}</span>
              </div>
              {item.product.recipientName && (
                <p className="text-xs text-purple-500">To: {item.product.recipientName} ({item.product.recipientEmail})</p>
              )}
            </div>
          ))}
        </div>
        <div className="border-t pt-3 space-y-2">
          <div className="flex justify-between text-sm"><span>Subtotal</span><span>{formatCurrency(cartTotal)}</span></div>
          {appliedCashback && <div className="flex justify-between text-sm text-emerald-600"><span>Cashback ({appliedCashback.percentage}%)</span><span>+{formatCurrency(cashbackAmount)}</span></div>}
          <div className="flex justify-between font-semibold"><span>Total</span><span>{formatCurrency(finalTotal)}</span></div>
        </div>
        {selectedPayment && (
          <div className="p-3 rounded-lg" style={{ backgroundColor: theme.creamDark }}>
            <div className="flex items-center justify-between">
              <span className="text-sm text-purple-500">Payment</span>
              <span className="text-sm font-medium">{selectedPayment.id === 'rewards' ? 'Bonndle Rewards' : `Card •••• ${selectedPayment.last4Digits || selectedPayment.last4}`}</span>
            </div>
          </div>
        )}
        <div className="flex gap-3 pt-2">
          <button onClick={onBack} className="flex-1 py-3 border border-purple-300 rounded-xl font-medium text-purple-700">Back</button>
          <button onClick={onProcess} disabled={processingPayment} className="flex-1 py-3 text-white rounded-xl font-semibold disabled:opacity-50 flex items-center justify-center gap-2" style={{ backgroundColor: theme.primary }}>
            {processingPayment ? <><Loader2 className="w-5 h-5 animate-spin" />Processing...</> : `Pay ${formatCurrency(finalTotal)}`}
          </button>
        </div>
      </div>
    </div>
  );
}

// Success Modal Component
function SuccessModal({ transactionDetails, onClose, theme }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-white">
      <div className="w-full max-w-md text-center">
        <div className="p-6 pt-12">
          <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Check size={32} className="text-emerald-600" />
          </div>
          <h2 className="text-xl font-bold text-purple-900 mb-2">Gift Card Purchased!</h2>
          <p className="text-purple-500 text-sm mb-6">Your purchase was successful.</p>
          <div className="p-4 rounded-xl text-left mb-5" style={{ backgroundColor: theme.creamDark }}>
            <div className="flex justify-between items-center mb-3">
              <span className="text-sm text-purple-500">Items</span>
              <span className="text-sm font-bold">{transactionDetails.items.length} item(s)</span>
            </div>
            <div className="flex justify-between items-center mb-3">
              <span className="text-sm text-purple-500">Amount Paid</span>
              <span className="text-sm font-bold">{formatCurrency(transactionDetails.amount)}</span>
            </div>
            {transactionDetails.cashbackEarned > 0 && (
              <div className="flex justify-between items-center mb-3">
                <span className="text-sm text-purple-500">Cashback Earned</span>
                <span className="text-sm font-bold text-emerald-600">+{formatCurrency(transactionDetails.cashbackEarned)}</span>
              </div>
            )}
            <div className="flex justify-between items-center">
              <span className="text-sm text-purple-500">Reference</span>
              <span className="text-sm font-bold">{transactionDetails.transactionId.slice(-12)}</span>
            </div>
          </div>
          {transactionDetails.cashbackEarned > 0 && (
            <div className="bg-emerald-100 px-4 py-2 rounded-full inline-flex items-center gap-2 mb-6">
              <Coins size={16} className="text-amber-500" />
              <span className="text-emerald-700 font-bold text-sm">You saved {formatCurrency(transactionDetails.cashbackEarned)}</span>
            </div>
          )}
          <button onClick={onClose} className="w-full py-3 text-white rounded-xl font-semibold" style={{ backgroundColor: theme.primary }}>Done</button>
        </div>
      </div>
    </div>
  );
}
